<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import WhatsappStickersAPI from 'dashboard/api/whatsappStickers';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'send']);

const { t } = useI18n();
const fileInput = ref(null);
const stickers = ref([]);
const stats = ref(null);
const isLoading = ref(false);
const isUploading = ref(false);

const sortedStickers = computed(() =>
  [...stickers.value].sort((a, b) => {
    if (a.favorite !== b.favorite) return a.favorite ? -1 : 1;
    return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
  })
);

const fetchStickers = async () => {
  isLoading.value = true;
  try {
    const [stickersResponse, statsResponse] = await Promise.all([
      WhatsappStickersAPI.get(),
      WhatsappStickersAPI.stats(),
    ]);
    stickers.value = stickersResponse.data || [];
    stats.value = statsResponse.data || null;
  } catch {
    useAlert(t('CONVERSATION.STICKERS.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const imageDimensions = file =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: image.width, height: image.height });
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('invalid-image'));
    };
    image.src = url;
  });

const isAnimatedWebp = async file => {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  const marker = [65, 78, 77, 70]; // ANMF

  return bytes.some((byte, index) =>
    marker.every((markerByte, offset) => bytes[index + offset] === markerByte)
  );
};

const validateStickerFile = async file => {
  if (!file || file.type !== 'image/webp') {
    throw new Error(t('CONVERSATION.STICKERS.ERRORS.WEBP'));
  }

  const dimensions = await imageDimensions(file);
  if (dimensions.width !== 512 || dimensions.height !== 512) {
    throw new Error(t('CONVERSATION.STICKERS.ERRORS.DIMENSIONS'));
  }

  const animated = await isAnimatedWebp(file);
  const maxSize = animated ? 500 * 1024 : 100 * 1024;
  if (file.size > maxSize) {
    throw new Error(
      animated
        ? t('CONVERSATION.STICKERS.ERRORS.ANIMATED_SIZE')
        : t('CONVERSATION.STICKERS.ERRORS.STATIC_SIZE')
    );
  }
};

const onUploadSelected = async event => {
  const [file] = Array.from(event.target.files || []);
  if (!file) return;

  try {
    await validateStickerFile(file);
    isUploading.value = true;
    const response = await WhatsappStickersAPI.createSticker({
      file,
      name: file.name.replace(/\.webp$/i, ''),
      favorite: true,
    });
    stickers.value = [response.data, ...stickers.value];
  } catch (error) {
    useAlert(error.message || t('CONVERSATION.STICKERS.UPLOAD_ERROR'));
  } finally {
    isUploading.value = false;
    event.target.value = '';
  }
};

const toggleFavorite = async sticker => {
  try {
    const response = await WhatsappStickersAPI.update(sticker.id, {
      favorite: !sticker.favorite,
    });
    stickers.value = stickers.value.map(item =>
      item.id === sticker.id ? response.data : item
    );
  } catch {
    useAlert(t('CONVERSATION.STICKERS.UPDATE_ERROR'));
  }
};

const deleteSticker = async sticker => {
  try {
    await WhatsappStickersAPI.delete(sticker.id);
    stickers.value = stickers.value.filter(item => item.id !== sticker.id);
  } catch {
    useAlert(t('CONVERSATION.STICKERS.DELETE_ERROR'));
  }
};

watch(
  () => props.show,
  show => {
    if (show) fetchStickers();
  },
  { immediate: true }
);
</script>

<template>
  <div>
    <div
      v-if="show"
      class="fixed inset-0 z-50 flex items-center justify-center bg-modal-backdrop-light p-4 dark:bg-modal-backdrop-dark"
      @click.self="emit('close')"
    >
      <section
        class="flex max-h-[80vh] w-full max-w-xl flex-col rounded-lg border border-n-weak bg-n-surface-1 shadow-xl"
      >
        <header
          class="flex items-center justify-between border-b border-n-weak px-4 py-3"
        >
          <div>
            <h3 class="text-heading-3 text-n-slate-12">
              {{ t('CONVERSATION.STICKERS.TITLE') }}
            </h3>
            <p class="mb-0 text-body-mini text-n-slate-11">
              {{ t('CONVERSATION.STICKERS.DESCRIPTION') }}
            </p>
          </div>
          <NextButton icon="i-lucide-x" slate faded sm @click="emit('close')" />
        </header>

        <div class="flex items-center justify-between gap-3 px-4 py-3">
          <input
            ref="fileInput"
            type="file"
            accept="image/webp"
            class="hidden"
            @change="onUploadSelected"
          />
          <NextButton
            icon="i-lucide-upload"
            :label="t('CONVERSATION.STICKERS.UPLOAD')"
            slate
            faded
            sm
            :is-loading="isUploading"
            @click="fileInput?.click()"
          />
          <span class="text-body-mini text-n-slate-11">
            {{ t('CONVERSATION.STICKERS.REQUIREMENTS') }}
          </span>
        </div>

        <div
          v-if="stats"
          class="grid grid-cols-2 gap-2 border-y border-n-weak px-4 py-3 text-body-mini text-n-slate-11 sm:grid-cols-5"
        >
          <div>
            <p class="mb-0 text-n-slate-10">
              {{ t('CONVERSATION.STICKERS.STATS.SAVED') }}
            </p>
            <strong class="text-n-slate-12">{{ stats.saved_total }}</strong>
          </div>
          <div>
            <p class="mb-0 text-n-slate-10">
              {{ t('CONVERSATION.STICKERS.STATS.FROM_CHAT') }}
            </p>
            <strong class="text-n-slate-12">
              {{ stats.saved_from_attachment_total }}
            </strong>
          </div>
          <div>
            <p class="mb-0 text-n-slate-10">
              {{ t('CONVERSATION.STICKERS.STATS.SENT') }}
            </p>
            <strong class="text-n-slate-12">
              {{ stats.sent_last_window }}
            </strong>
          </div>
          <div>
            <p class="mb-0 text-n-slate-10">
              {{ t('CONVERSATION.STICKERS.STATS.FAILED') }}
            </p>
            <strong class="text-n-slate-12">
              {{ stats.failed_last_window }}
            </strong>
          </div>
          <div>
            <p class="mb-0 text-n-slate-10">
              {{ t('CONVERSATION.STICKERS.STATS.OPT_OUT') }}
            </p>
            <strong class="text-n-slate-12">
              {{ stats.sent_to_opted_out_last_window }}
            </strong>
          </div>
        </div>

        <div class="min-h-40 overflow-y-auto px-4 pb-4">
          <div
            v-if="isLoading"
            class="flex h-40 items-center justify-center text-body-main text-n-slate-11"
          >
            {{ t('CONVERSATION.STICKERS.LOADING') }}
          </div>
          <div
            v-else-if="!sortedStickers.length"
            class="flex h-40 items-center justify-center text-body-main text-n-slate-11"
          >
            {{ t('CONVERSATION.STICKERS.EMPTY') }}
          </div>
          <div v-else class="grid grid-cols-3 gap-3 sm:grid-cols-4">
            <div
              v-for="sticker in sortedStickers"
              :key="sticker.id"
              class="group relative aspect-square rounded-lg border border-n-weak bg-n-alpha-1 p-2"
            >
              <button
                class="flex h-full w-full items-center justify-center"
                type="button"
                @click="emit('send', sticker)"
              >
                <img
                  :src="sticker.thumb_url || sticker.file_url"
                  :alt="sticker.name"
                  class="max-h-full max-w-full object-contain"
                />
              </button>
              <div class="absolute right-1 top-1 flex gap-1">
                <NextButton
                  :icon="sticker.favorite ? 'i-lucide-star' : 'i-lucide-star'"
                  :variant="sticker.favorite ? 'solid' : 'faded'"
                  color="slate"
                  xs
                  @click.stop="toggleFavorite(sticker)"
                />
                <NextButton
                  icon="i-lucide-trash-2"
                  ruby
                  faded
                  xs
                  @click.stop="deleteSticker(sticker)"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>
